import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseScopeItem } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  LoadingState,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { AddScopeDrawer } from "./AddScopeDrawer";
import { RemoveScopeDrawer } from "./RemoveScopeDrawer";
import { RecordQaDrawer } from "./RecordQaDrawer";
import { QaDispositionDrawer } from "./QaDispositionDrawer";
import { ScopeChangeLedger } from "./ScopeChangeLedger";
import {
  QA_DISPOSITION_LABELS,
  QA_STATUS_LABELS,
  QA_STATUS_TONE,
  RELATION_LABELS,
} from "./releaseVocabulary";

/** §19's filters, each answering a question somebody actually asks while chasing a release. */
const FILTERS: { key: string; label: string; match: (item: ReleaseScopeItem) => boolean }[] = [
  { key: "all", label: "All", match: () => true },
  { key: "ready", label: "Ready", match: (item) => item.ready },
  { key: "not-ready", label: "Not ready", match: (item) => !item.ready },
  { key: "dev-pending", label: "Development pending", match: (item) => item.status !== "DONE" },
  {
    key: "qa-pending",
    label: "QA pending",
    match: (item) =>
      item.qaRequired === "REQUIRED" && item.qaStatus !== "PASSED" && item.qaStatus !== "FAILED",
  },
  { key: "qa-failed", label: "QA failed", match: (item) => item.qaStatus === "FAILED" },
  { key: "after-freeze", label: "Added after freeze", match: (item) => item.addedAfterScopeFreeze },
];

/**
 * §19's scope table: every ticket, and whether it is holding the release up.
 *
 * <p>Filtering happens in the browser here, unlike the other listings in this application, and
 * deliberately: a release's scope is bounded by what somebody chose to put in it — tens or low
 * hundreds — and it is already loaded to compute the tallies above. Paging it server-side would
 * cost a round trip per filter click to narrow a list that is already in hand.
 */
export function ReleaseScopeTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ReleaseScopeItem | null>(null);
  const [testing, setTesting] = useState<ReleaseScopeItem | null>(null);
  const [disposition, setDisposition] = useState<ReleaseScopeItem | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const scope = useQuery({
    queryKey: ["release-scope", release.id],
    queryFn: () => api.get<ReleaseScopeItem[]>(`/api/v1/releases/${release.id}/scope`),
  });

  /*
   * §14: what was taken out, kept where somebody can find it. A removed item vanishing from every
   * screen is how "why isn't that in the release?" becomes unanswerable a month later.
   */
  const removed = useQuery({
    queryKey: ["release-scope-removed", release.id],
    queryFn: () =>
      api.get<ReleaseScopeItem[]>(`/api/v1/releases/${release.id}/scope/removed`),
    enabled: showRemoved,
  });

  const rows = useMemo(() => {
    const match = FILTERS.find((entry) => entry.key === filter)?.match ?? (() => true);
    return (scope.data ?? []).filter(match);
  }, [scope.data, filter]);

  const columns: Column<ReleaseScopeItem>[] = [
    {
      key: "itemKey",
      header: "Item",
      render: (item) => (
        <button
          type="button"
          onClick={() => navigate(`/work-items/${item.itemKey}`)}
          className="font-mono text-xs text-brand-600 hover:underline"
        >
          {item.itemKey}
        </button>
      ),
    },
    {
      key: "title",
      header: "Title",
      render: (item) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{item.title}</p>
          {item.relationType && item.relationType !== "PRIMARY_TARGET" && (
            <p className="text-xs text-ink-500">{RELATION_LABELS[item.relationType]}</p>
          )}
        </div>
      ),
    },
    { key: "assignee", header: "Assignee", render: (item) => item.assigneeName ?? "—" },
    { key: "status", header: "Development", render: (item) => item.status.replace(/_/g, " ") },
    {
      key: "qaRequired",
      header: "QA",
      render: (item) =>
        item.qaRequired == null ? "—" : (
          <StatusBadge tone={item.qaRequired === "REQUIRED" ? "gray" : "violet"}>
            {QA_DISPOSITION_LABELS[item.qaRequired]}
          </StatusBadge>
        ),
    },
    {
      key: "qaStatus",
      header: "QA result",
      render: (item) =>
        item.qaStatus == null ? (
          <span className="text-ink-400">—</span>
        ) : (
          <StatusBadge tone={QA_STATUS_TONE[item.qaStatus]}>
            {QA_STATUS_LABELS[item.qaStatus]}
          </StatusBadge>
        ),
    },
    {
      key: "ready",
      header: "Release readiness",
      render: (item) => (
        <StatusBadge tone={item.ready ? "green" : "amber"}>
          {item.ready ? "Ready" : "Not ready"}
        </StatusBadge>
      ),
    },
    {
      key: "afterFreeze",
      header: "After freeze",
      render: (item) =>
        item.addedAfterScopeFreeze ? (
          <StatusBadge tone="amber">Yes</StatusBadge>
        ) : (
          <span className="text-ink-400">No</span>
        ),
    },
    {
      key: "actions",
      header: "",
      render: (item) => (
        <div className="flex justify-end gap-1">
          {can("RELEASE_QA_UPDATE") && item.qaRequired === "REQUIRED" && (
            <Button variant="ghost" size="sm" onClick={() => setTesting(item)}>
              Record QA
            </Button>
          )}
          {can("RELEASE_QA_UPDATE") && item.id && (
            <Button
              variant="ghost"
              size="sm"
              title="Say whether this needs testing"
              onClick={() => setDisposition(item)}
            >
              Testing?
            </Button>
          )}
          {can("RELEASE_SCOPE_EDIT") && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${item.itemKey}`}
              title="Take out of this release"
              onClick={() => setRemoving(item)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
    <Card>
      <CardHeader
        title="Scope"
        subtitle={`${release.scope.total} items · ${release.scope.completed} complete`}
        action={
          can("RELEASE_SCOPE_EDIT") && (
            <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              Add work
            </Button>
          )
        }
      />
      <CardBody className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((entry) => {
            const count = (scope.data ?? []).filter(entry.match).length;
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => setFilter(entry.key)}
                className={
                  "rounded-full px-2.5 py-1 text-xs font-medium transition-colors "
                  + (entry.key === filter
                    ? "bg-brand-600 text-white"
                    : "bg-ink-100 text-ink-600 hover:bg-ink-200")
                }
              >
                {entry.label}
                <span className="ml-1 tabular-nums opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(item) => item.id ?? item.workItemId}
          loading={scope.isPending}
          emptyTitle={filter === "all" ? "Nothing in this release yet" : "Nothing matches"}
          emptyMessage={
            filter === "all"
              ? "Add the work this release will carry. Scope can change freely until it is frozen."
              : "Try another filter."
          }
        />
      </CardBody>

      <AddScopeDrawer
        release={release}
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={onChanged}
      />
      <RemoveScopeDrawer
        release={release}
        item={removing}
        onClose={() => setRemoving(null)}
        onSaved={onChanged}
      />
      <RecordQaDrawer
        release={release}
        item={testing}
        onClose={() => setTesting(null)}
        onSaved={onChanged}
      />
      <QaDispositionDrawer
        release={release}
        item={disposition}
        onClose={() => setDisposition(null)}
        onSaved={onChanged}
      />
    </Card>

    <Card>
      <CardHeader
        title="Removed from this release"
        subtitle="Work that was in scope and was taken out, with the reason given."
        action={
          <Button size="sm" variant="secondary" onClick={() => setShowRemoved((on) => !on)}>
            {showRemoved ? "Hide" : "Show"}
          </Button>
        }
      />
      {showRemoved && (
        <CardBody>
          {removed.isPending ? (
            <LoadingState label="Loading removed work…" />
          ) : (removed.data ?? []).length === 0 ? (
            <EmptyState
              title="Nothing has been removed"
              message="Anything taken out of this release will be listed here."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {(removed.data ?? []).map((item) => (
                <li key={item.workItemId} className="py-2.5">
                  <p className="text-sm text-ink-900">
                    <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>{" "}
                    {item.title}
                  </p>
                  {item.qaDispositionReason && (
                    <p className="mt-0.5 text-xs text-ink-600">{item.qaDispositionReason}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      )}
    </Card>

    <ScopeChangeLedger release={release} />
    </div>
  );
}
